import { default as axios } from './axiosConfig'
import { getEnv } from '@utils/env'
import type {
  FormGroup,
  FormGroupInput,
  IFormTemplate,
  ListFormTemplatesParams,
} from '@dataTypes/forms.type'
import type { PendingFormEntry } from '@pages/FormBuilder/assignedForms.types'

export type FormsListResponse = {
  statusCode: number
  message: string
  data: { formTemplates: IFormTemplate[] }
}

export type FormTemplateResponse = {
  statusCode: number
  message: string
  data: { formTemplate: IFormTemplate | null }
}

const BUSINESS_LAYER_FORMS = getEnv('REACT_APP_BUSINESS_LAYER_FORMS')
const headers = { 'Content-Type': 'application/json' }

const applyTenantHeader = (
  requestHeaders: Record<string, string | boolean>,
): void => {
  const tenantId = localStorage.getItem('tenant_id')
  if (tenantId) requestHeaders['X-Tenant-ID'] = tenantId
}

export const getFormTemplates = async (
  params?: ListFormTemplatesParams,
): Promise<IFormTemplate[]> => {
  const { data } = await axios.get<FormsListResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/templates`,
    { headers, params: params ?? {} },
  )
  return data?.data?.formTemplates ?? []
}

export const getFormTemplateById = async (
  id: string,
): Promise<IFormTemplate | null> => {
  const { data } = await axios.get<FormTemplateResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/templates/${id}`,
    { headers },
  )
  return data?.data?.formTemplate ?? null
}

export const createFormTemplate = async (
  payload: Partial<IFormTemplate>,
): Promise<IFormTemplate | null> => {
  const { data } = await axios.post<FormTemplateResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/templates`,
    payload,
    { headers },
  )
  return data?.data?.formTemplate ?? null
}

export const createFormTemplateVersion = async (
  sourceFormTemplateId: string,
  payload: {
    title: string
    category_title: string
    schema_json: IFormTemplate['schema_json']
    status?: IFormTemplate['status']
  },
): Promise<IFormTemplate> => {
  const { data } = await axios.post<FormTemplateResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/templates`,
    {
      source_form_template_id: sourceFormTemplateId,
      ...payload,
    },
    { headers },
  )
  const template = data?.data?.formTemplate
  if (!template) throw new Error('Failed to create form template version.')
  return template
}

export const updateFormTemplateStatus = async (
  formTemplateId: string,
  status: IFormTemplate['status'],
  approvedBy?: string | null,
): Promise<void> => {
  const body: { status: IFormTemplate['status']; approved_by?: string } = {
    status,
  }
  if (status === 'approved' && approvedBy) {
    body.approved_by = approvedBy
  }
  await axios.patch(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/templates/${formTemplateId}/status`,
    body,
    { headers },
  )
}

export const deleteFormTemplate = async (id: string): Promise<void> => {
  await axios.delete(`${BUSINESS_LAYER_FORMS}/api/v1/forms/templates/${id}`, {
    headers,
  })
}

export const bulkDeleteFormTemplates = async (
  formTemplateIds: string[],
): Promise<void> => {
  await axios.delete(`${BUSINESS_LAYER_FORMS}/api/v1/forms/templates/bulk`, {
    headers,
    data: { form_template_ids: formTemplateIds },
  })
}

type FormGroupsResponse = {
  data: { formGroups: FormGroup[] }
}

type FormGroupResponse = {
  data: { formGroup: FormGroup }
}

export const getFormGroups = async (): Promise<FormGroup[]> => {
  const { data } = await axios.get<FormGroupsResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/form-groups`,
    { headers },
  )
  return data?.data?.formGroups ?? []
}

export const createFormGroup = async (
  payload: FormGroupInput,
): Promise<FormGroup> => {
  const { data } = await axios.post<FormGroupResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/form-groups`,
    payload,
    { headers },
  )
  return data.data.formGroup
}

export const updateFormGroup = async (
  id: string,
  payload: FormGroupInput,
): Promise<FormGroup> => {
  const { data } = await axios.put<FormGroupResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/form-groups/${id}`,
    payload,
    { headers },
  )
  return data.data.formGroup
}

export const deleteFormGroup = async (id: string): Promise<void> => {
  await axios.delete(`${BUSINESS_LAYER_FORMS}/api/v1/forms/form-groups/${id}`, {
    headers,
  })
}

export const setNewPatientKioskFormGroup = async (
  formGroupId: string | null,
): Promise<void> => {
  await axios.put(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/settings/new-patient-kiosk-group`,
    { formGroupId },
    { headers },
  )
}

// ─── Audit timeline ─────────────────────────────────────────────────────────

export interface AuditEventEntry {
  auditEventId: string
  assignmentId: string
  patientId: string
  formTemplateId: string
  eventType: string
  actorType: string
  actorUserId: string | null
  actorPatientId: string | null
  actorDisplayName: string | null
  actorEmail: string | null
  source: string | null
  status: string | null
  ipAddress: string | null
  userAgent: string | null
  documentHashSha256: string | null
  blobUri: string | null
  templateVersion: number | null
  eventPayloadJson: Record<string, unknown> | null
  createdAt: string
}

export interface AuditTimelineResponse {
  statusCode: number
  message: string
  data: { events: AuditEventEntry[] }
}

export const getAssignmentAuditTimeline = async (
  assignmentId: string,
): Promise<AuditEventEntry[]> => {
  const { data } = await axios.get<AuditTimelineResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/audit-trail/${assignmentId}`,
    { headers },
  )
  return data?.data?.events ?? []
}

export interface CreateAssignmentsParams {
  patient_id: string
  appointment_ids: string[]
  form_template_ids: string[]
}

export interface CreateAssignmentsResponse {
  statusCode: number
  message: string
  data: { created: number; assignmentIds: string[] }
}

export interface SendInvitationParams extends CreateAssignmentsParams {
  channel: 'kiosk' | 'mobile' | 'email'
  recipient_phone?: string
  ttl_seconds?: number
}

export interface SendInvitationResponse {
  statusCode: number
  message: string
  data: {
    created: number
    assignmentIds: string[]
    expires_at: string
    delivery_status: 'generated'
  }
}

export const createFormAssignments = async (
  params: CreateAssignmentsParams,
): Promise<{ created: number; assignmentIds: string[] }> => {
  const { data } = await axios.post<CreateAssignmentsResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments`,
    params,
    { headers },
  )
  return data?.data ?? { created: 0, assignmentIds: [] }
}

export const sendFormAssignmentsInvitation = async (
  params: SendInvitationParams,
): Promise<{
  created: number
  assignmentIds: string[]
  expires_at: string
  delivery_status: 'generated'
}> => {
  const invitationHeaders: Record<string, string> = { ...headers }
  const clinicId = localStorage.getItem('clinic_id')
  const dbName = localStorage.getItem('db_name')
  const tenantId = localStorage.getItem('tenant_id')
  if (clinicId) {
    invitationHeaders['X-Clinic-ID'] = clinicId
  }
  if (dbName) {
    invitationHeaders['X-Db-Name'] = dbName
  }
  if (tenantId) {
    invitationHeaders['X-Tenant-ID'] = tenantId
  }
  const { data } = await axios.post<SendInvitationResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/send-invitation`,
    params,
    { headers: invitationHeaders },
  )
  return (
    data?.data ?? {
      created: 0,
      assignmentIds: [],
      expires_at: '',
      delivery_status: 'generated',
    }
  )
}

export interface ListPendingResponse {
  statusCode: number
  message: string
  data: { entries: PendingFormEntry[] }
}

export const getPendingFormEntries = async (params?: {
  patient_id?: string
  appointment_id?: string
}): Promise<PendingFormEntry[]> => {
  const { data } = await axios.get<ListPendingResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/pending`,
    { headers, params: params ?? {} },
  )
  return data?.data?.entries ?? []
}

/** Kiosk: one assigned form with full fields for rendering. id is assignmentId for submit. */
export interface KioskAssignmentItem {
  assignmentId: string
  clinicId: string
  appointmentId: string | null
  formTemplateId: string
  name: string
  fields: {
    section: string
    items: {
      label: string
      value: string | null
      options?: string[]
      selected?: string
      checked?: boolean
      isCheckbox?: boolean
      rowGroup?: number
      isRichText?: boolean
      isDate?: boolean
    }[]
  }[]
  /** Raw schema for schema-native rendering (GroupField[] as JSON). */
  schema_json?: string | Record<string, unknown> | unknown[]
  /** Pre-filled values by field id from patient (backend autofill). Used when patientId not in URL. */
  initial_field_values?: Record<string, string>
  /** Patient full name from backend (from patient_id) for signature block and display. */
  patient_name?: string
}

export interface KioskClinicNumberHeader {
  phone_number: string
  status: string
  is_main?: boolean
}

export interface KioskClinicHeader {
  clinic_id: string
  clinic_name: string
  address: string
  city: string
  state: string
  zip: string
  phone_number?: string
  clinic_number?: KioskClinicNumberHeader[]
}

export interface GetKioskAssignmentsResponse {
  statusCode: number
  message: string
  data: {
    assignments: KioskAssignmentItem[]
    clinic?: KioskClinicHeader | null
  }
}

export interface NewPatientKioskAssignmentsResult {
  configured: boolean
  groupId: string | null
  created: number
  assignmentIds: string[]
}

export const prepareNewPatientKioskAssignments = async (
  accessToken: string,
  patientId: string,
  options?: { clinicId?: string | null; dbName?: string | null },
): Promise<NewPatientKioskAssignmentsResult> => {
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
  if (options?.clinicId) requestHeaders['X-Clinic-ID'] = options.clinicId
  if (options?.dbName) requestHeaders['X-Db-Name'] = options.dbName
  applyTenantHeader(requestHeaders)
  const { data } = await axios.post<{
    data: NewPatientKioskAssignmentsResult
  }>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/new-patient-kiosk`,
    { patient_id: patientId },
    { headers: requestHeaders },
  )
  return data.data
}

export interface CompleteKioskAssignmentResponse {
  statusCode: number
  message: string
  data: Record<string, unknown>
}

/**
 * Load assigned forms for the OTP-authenticated patient. Uses explicit token so kiosk does not overwrite staff session.
 */
export const getKioskAssignments = async (
  accessToken: string,
  options?: {
    clinicId?: string | null
    dbName?: string | null
    patientId?: string | null
  },
): Promise<{
  assignments: KioskAssignmentItem[]
  clinic: KioskClinicHeader | null
}> => {
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
  if (options?.clinicId) {
    requestHeaders['X-Clinic-ID'] = options.clinicId
  }
  if (options?.dbName) {
    requestHeaders['X-Db-Name'] = options.dbName
  }
  applyTenantHeader(requestHeaders)
  const { data } = await axios.get<GetKioskAssignmentsResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/kiosk`,
    {
      headers: requestHeaders,
      params: { ...(options?.patientId && { patient_id: options.patientId }) },
    },
  )
  return {
    assignments: data?.data?.assignments ?? [],
    clinic: data?.data?.clinic ?? null,
  }
}

/** Supplemental signer context sent alongside the form payload.
 *  The backend is authoritative for official audit timestamps and IP.
 *  These fields are treated as untrusted supporting evidence only.
 */
export interface KioskAuditContext {
  clinic_id?: string
  patient_id?: string
  patient_name?: string
  appointment_id?: string
  form_template_id?: string
  consent_accepted?: boolean
  terms_version?: string
  /** Client-side ISO timestamp – supplemental only, not the official record time. */
  accepted_at?: string
  signature_method?: string
  /** IANA timezone string, e.g. "America/Los_Angeles". */
  client_timezone?: string
  session_id?: string
}

/**
 * Mark one assignment as completed with the submitted form payload. Uses explicit token for kiosk.
 * Pass audit_context to enable server-side CONSENT_ACCEPTED and SUBMISSION_COMPLETED events.
 */
export const completeKioskAssignment = async (
  accessToken: string,
  assignmentId: string,
  completedPayload: Record<string, unknown>,
  options?: { clinicId?: string | null; dbName?: string | null },
  auditContext?: KioskAuditContext,
): Promise<void> => {
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
  if (options?.clinicId) {
    requestHeaders['X-Clinic-ID'] = options.clinicId
  }
  if (options?.dbName) {
    requestHeaders['X-Db-Name'] = options.dbName
  }
  applyTenantHeader(requestHeaders)
  await axios.patch<CompleteKioskAssignmentResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/${assignmentId}/complete`,
    {
      completed_payload: completedPayload,
      ...(auditContext && { audit_context: auditContext }),
    },
    { headers: requestHeaders },
  )
}

export interface CreateSignedFormUploadUrlResponse {
  statusCode: number
  message: string
  data: { upload_url: string; blob_name: string }
}

export const createSignedFormUploadUrl = async (
  assignmentId: string,
  filename: string,
  options: {
    clinicId: string
    patientId: string
    dbName?: string | null
    accessToken?: string | null
  },
): Promise<{ upload_url: string; blob_name: string }> => {
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (options.accessToken)
    requestHeaders['Authorization'] = `Bearer ${options.accessToken}`
  if (options.clinicId) requestHeaders['X-Clinic-ID'] = options.clinicId
  if (options.dbName) requestHeaders['X-Db-Name'] = options.dbName
  applyTenantHeader(requestHeaders)
  const { data } = await axios.post<CreateSignedFormUploadUrlResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/upload-url`,
    { assignment_id: assignmentId, filename, patient_id: options.patientId },
    { headers: requestHeaders },
  )
  return data?.data ?? { upload_url: '', blob_name: '' }
}

export interface UploadSignedFormPdfAuditContext {
  assignmentId: string
  clinicId: string
  patientId: string
  appointmentId?: string | null
  formTemplateId: string
  documentHashSha256: string
}

export interface UploadSignedFormPdfResponse {
  statusCode: number
  message: string
  data: { url: string; blob_name: string }
}

export const uploadSignedFormPdf = async (
  file: Blob,
  filename: string,
  auditContext: UploadSignedFormPdfAuditContext,
  options?: {
    clinicId?: string | null
    dbName?: string | null
    accessToken?: string | null
  },
): Promise<{ url: string; blob_name: string }> => {
  const formData = new FormData()
  formData.append('file', file, 'signed-form.pdf')
  formData.append('file_name', filename)
  formData.append(
    'audit_context',
    JSON.stringify({
      clinic_id: auditContext.clinicId,
      assignment_id: auditContext.assignmentId,
      patient_id: auditContext.patientId,
      appointment_id: auditContext.appointmentId ?? null,
      form_template_id: auditContext.formTemplateId,
      document_hash_sha256: auditContext.documentHashSha256,
    }),
  )
  const requestHeaders: Record<string, string | boolean> = {}
  if (options?.accessToken)
    requestHeaders['Authorization'] = `Bearer ${options.accessToken}`
  if (options?.clinicId) requestHeaders['X-Clinic-ID'] = options.clinicId
  if (options?.dbName) requestHeaders['X-Db-Name'] = options.dbName
  applyTenantHeader(requestHeaders)
  // Let browser set Content-Type to multipart/form-data with boundary (required for file upload)
  requestHeaders['Content-Type'] = false
  const { data } = await axios.post<UploadSignedFormPdfResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/upload`,
    formData,
    { headers: requestHeaders },
  )
  return data?.data ?? { url: '', blob_name: '' }
}

export interface GetCompletedFormDownloadUrlResponse {
  statusCode: number
  message: string
  data: { download_url: string }
}

export const getCompletedFormDownloadUrl = async (
  assignmentId: string,
  options?: { dbName?: string | null },
): Promise<{ download_url: string }> => {
  const requestHeaders: Record<string, string> = {}
  if (options?.dbName) requestHeaders['X-Db-Name'] = options.dbName
  applyTenantHeader(requestHeaders)
  const { data } = await axios.get<GetCompletedFormDownloadUrlResponse>(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/${assignmentId}/download-url`,
    { headers: requestHeaders },
  )
  return data?.data ?? { download_url: '' }
}

export const regenerateSignedFormPdf = async (
  assignmentId: string,
): Promise<void> => {
  const requestHeaders: Record<string, string> = {}
  applyTenantHeader(requestHeaders)
  await axios.post(
    `${BUSINESS_LAYER_FORMS}/api/v1/forms/assignments/${assignmentId}/signed-pdf/regenerate`,
    {},
    { headers: requestHeaders },
  )
}
